'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { BodyShort, Button, Detail, HelpText, HStack } from '@navikt/ds-react';
import styles from './SeksjonsListe.module.css';
import { store } from '@/app/_components/store/store';
import { hjelpetekstAriaLabel } from '@/utils/formatting/tekst';
import classNames from 'classnames';

/** Strukturelt kompatibel med feltene til SortedTable, slik at samme array kan mate begge visningene. */
export type SeksjonsFelt<T> = {
  label?: React.ReactNode;
  hjelpetekst?: React.ReactNode;
  getValue: (data: T, index: number, id: string | number) => React.ReactNode;
  sortValue?: (data: T) => string | number | undefined | null;
  /** Brukes bare av SortedTable. Lista ignorerer disse. */
  sortable?: boolean;
  minWidth?: string | number;
};
type Props<T> = {
  data: T[];
  felter: SeksjonsFelt<T>[];
  /** Feltet som utheves og sorteres på. */
  noekkel: number;
  itemsToShow?: number;
};
const sorterNyesteFoerst = <T,>(data: T[], felt?: SeksjonsFelt<T>) => {
  if (!felt?.sortValue) return data;
  return [...data].sort((a, b) => {
    const verdiA = felt.sortValue!(a);
    const verdiB = felt.sortValue!(b);
    if (verdiA == null && verdiB == null) return 0;
    if (verdiA == null) return 1;
    if (verdiB == null) return -1;
    return String(verdiB).localeCompare(String(verdiA), 'nb', {
      numeric: true
    });
  });
};

/** Stablet visning av seksjonsdata. Erstatter den brede tabellen på små skjermer. */
export default function SeksjonsListe(t0) {
  const $ = _c(28);
  const {
    data,
    felter,
    noekkel,
    itemsToShow
  } = t0;
  const [visAlle, setVisAlle] = React.useState(false);
  const [antallVisninger, setAntallVisninger] = React.useState(1);
  const {
    tekst
  } = store.useReducerState();
  const t1 = felter[noekkel];
  let t2;
  if ($[0] !== data || $[1] !== t1) {
    t2 = sorterNyesteFoerst(data, t1);
    $[0] = data;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const sortert = t2;
  const antallSynlige = !itemsToShow || visAlle ? sortert.length : Math.min(itemsToShow * antallVisninger, sortert.length);
  const antallGjenstaaende = sortert.length - antallSynlige;
  let t3;
  let t4;
  let t5;
  if ($[3] !== antallSynlige || $[4] !== felter || $[5] !== noekkel || $[6] !== sortert || $[7] !== tekst) {
    const synlige = sortert.slice(0, antallSynlige);
    t5 = styles.wrapper;
    t3 = styles.liste;
    let t6;
    if ($[11] !== felter || $[12] !== noekkel || $[13] !== tekst) {
      t6 = (rad, radIndex) => <li key={radIndex}><dl className={styles.rader}>{felter.map((felt, feltIndex) => <div className={classNames(styles.rad, feltIndex === noekkel && styles.noekkel)} key={feltIndex}><Detail as="dt" className={styles.label}>{felt.label}{felt.hjelpetekst && radIndex === 0 && <HelpText title={hjelpetekstAriaLabel(tekst, typeof felt.label === "string" ? felt.label : undefined)}>{felt.hjelpetekst}</HelpText>}</Detail><BodyShort as="dd" className={styles.verdi}>{felt.getValue(rad, radIndex, radIndex)}</BodyShort></div>)}</dl></li>;
      $[11] = felter;
      $[12] = noekkel;
      $[13] = tekst;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    t4 = synlige.map(t6);
    $[3] = antallSynlige;
    $[4] = felter;
    $[5] = noekkel;
    $[6] = sortert;
    $[7] = tekst;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t4) {
    t6 = <ul className={t3}>{t4}</ul>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== antallGjenstaaende || $[19] !== antallVisninger || $[20] !== itemsToShow || $[21] !== sortert.length || $[22] !== tekst) {
    t7 = itemsToShow && sortert.length > itemsToShow && <HStack gap="space-8">{antallGjenstaaende > 0 && <Button variant="tertiary" size="small" onClick={() => setAntallVisninger(antallVisninger + 1)}>{tekst.tabell.visFlere.replace("%s", String(Math.min(itemsToShow, antallGjenstaaende)))}</Button>}<Button variant="tertiary" size="small" onClick={() => {
        if (antallGjenstaaende > 0) {
          setVisAlle(true);
        } else {
          setVisAlle(false);
          setAntallVisninger(1);
        }
      }}>{antallGjenstaaende > 0 ? tekst.tabell.visAlle.replace("%s", String(sortert.length)) : tekst.tabell.visMindre}</Button></HStack>;
    $[18] = antallGjenstaaende;
    $[19] = antallVisninger;
    $[20] = itemsToShow;
    $[21] = sortert.length;
    $[22] = tekst;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t5 || $[25] !== t6 || $[26] !== t7) {
    t8 = <div className={t5}>{t6}{t7}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
}